<template>
  <form
    role="search"
    class="SearchField input"
    :aria-label="label || $t('nav.search')"
    @submit.prevent="$emit('submit', $refs.input.value)"
  >
    <button
      class="button-unstyled search-submit"
      type="submit"
      :title="$t('nav.search')"
    >
      <FAIcon
        fixed-width
        icon="search"
      />
    </button>
    <MentionPill
      v-if="account"
      :id="chipId"
      class="search-account"
      :users="[account]"
    >
      <NameWithEmoji
        class="mention-pill-name"
        :name="nameOf(account)"
        :emoji="account.emoji"
      />
      <button
        class="button-unstyled search-account-remove"
        type="button"
        :title="$t('search.remove_account', { name: nameOf(account) })"
        :aria-label="$t('search.remove_account', { name: nameOf(account) })"
        @click.prevent.stop="removeAccount"
      >
        <FAIcon icon="times" />
      </button>
    </MentionPill>
    <input
      :id="inputId"
      ref="input"
      :value="modelValue"
      class="search-field-input unstyled"
      :placeholder="shownPlaceholder"
      :aria-label="$t('nav.search')"
      type="text"
      enterkeyhint="search"
      role="combobox"
      :aria-expanded="suggestions.length > 0"
      :aria-controls="suggestions.length ? listId : undefined"
      :aria-activedescendant="suggestions.length ? optionId(highlighted) : undefined"
      :aria-describedby="account ? chipId : undefined"
      aria-autocomplete="list"
      @input="onInput"
      @keydown="onKeydown"
      @blur="closeList"
    >
    <button
      v-if="modelValue || closable"
      class="button-unstyled search-clear"
      type="button"
      :title="clearLabel"
      :aria-label="clearLabel"
      @click.prevent.stop="clearOrClose"
    >
      <FAIcon
        fixed-width
        icon="times"
      />
    </button>
    <div
      v-if="suggestions.length"
      :id="listId"
      class="search-suggestions popover popover-default autocomplete-panel-body"
      role="listbox"
    >
      <div
        v-for="(suggestion, index) in suggestions"
        :id="optionId(index)"
        :key="suggestion.user.id"
        class="menu-item autocomplete-item"
        :class="{ '-active': index === highlighted }"
        role="option"
        :aria-selected="index === highlighted"
        @mousedown.prevent="pick(suggestion.user)"
      >
        <span class="image -user">
          <img
            :src="suggestion.user.profile_image_url"
            alt=""
          >
        </span>
        <div class="label">
          <NameWithEmoji
            class="displayText -name"
            :name="nameOf(suggestion.user)"
            :emoji="suggestion.user.emoji"
          />
          <span class="detailText">@{{ suggestion.user.screen_name_ui }}</span>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import { suggestUsers } from 'src/components/emoji_input/suggestor.js'
import MentionPill from 'src/components/mention_pill/mention_pill.vue'
import NameWithEmoji from 'src/components/name_with_emoji/name_with_emoji.vue'

import {
  mentionAt,
  withoutRange,
} from 'src/services/search_account/search_account.js'
import { displayName } from 'src/services/user_display/user_display.js'

let lists = 0

// One pill-shaped search field: the magnifier searches, the cross clears
// the text, or closes the field when it is empty and closable. Typing
// @name suggests accounts; a pick becomes a chip that limits the search
// to that account's posts (v-model:account).
export default {
  components: { MentionPill, NameWithEmoji },
  props: {
    modelValue: { type: String, default: '' },
    // names the search landmark when a page has more than one
    label: { type: String, default: '' },
    inputId: { type: String, default: undefined },
    // the field can be put away (Escape, or the cross on an empty field)
    closable: Boolean,
    // the account whose posts are searched, a user object
    account: { type: Object, default: null },
  },
  emits: ['update:modelValue', 'update:account', 'submit', 'close'],
  expose: ['focus', 'blur'],
  data: () => ({
    suggestions: [],
    highlighted: 0,
    // the @word being typed: { start, end, query }
    mention: null,
    listId: `search-suggestions-${++lists}`,
    chipId: `search-account-${lists}`,
  }),
  created() {
    this.suggest = suggestUsers()
    // counts suggestion requests; only the newest one is shown
    this.requests = 0
  },
  computed: {
    clearLabel() {
      return this.modelValue
        ? this.$t('nav.search_clear')
        : this.$t('nav.search_close')
    },
    shownPlaceholder() {
      return this.$t(this.account ? 'search.their_posts' : 'nav.search')
    },
  },
  methods: {
    focus() {
      this.$refs.input?.focus()
    },
    nameOf: displayName,
    optionId(index) {
      return `${this.listId}-${index}`
    },
    // also drops a reply still on its way
    closeList() {
      this.requests++
      this.suggestions = []
    },
    blur() {
      this.$refs.input?.blur()
    },
    clearOrClose() {
      if (this.modelValue) {
        this.$emit('update:modelValue', '')
        this.focus()
      } else {
        this.$emit('close')
      }
    },
    async onInput(event) {
      const { value, selectionStart } = event.target
      this.$emit('update:modelValue', value)
      const mention = mentionAt(value, selectionStart ?? value.length)
      this.mention = mention
      if (!mention) {
        this.closeList()
        return
      }
      const request = ++this.requests
      const found = await this.suggest(`@${mention.query}`)
      // a reply to an older word is dropped
      if (request !== this.requests) return
      this.suggestions = found.slice(0, 6)
      this.highlighted = 0
    },
    onKeydown(event) {
      // Enter confirms the text of an input method, not a pick (Safari
      // sends that key with only the 229 code)
      if (event.isComposing || event.keyCode === 229) return
      const open = this.suggestions.length > 0
      const step = { ArrowDown: 1, ArrowUp: -1 }[event.key]
      if (open && step) {
        event.preventDefault()
        const count = this.suggestions.length
        this.highlighted = (this.highlighted + step + count) % count
      } else if (
        open &&
        (event.key === 'Enter' || (event.key === 'Tab' && !event.shiftKey))
      ) {
        event.preventDefault()
        this.pick(this.suggestions[this.highlighted].user)
      } else if (event.key === 'Escape') {
        if (open) this.closeList()
        else if (this.closable) this.$emit('close')
      } else if (
        event.key === 'Backspace' &&
        this.account &&
        event.target.selectionStart === 0 &&
        event.target.selectionEnd === 0
      ) {
        this.removeAccount()
      }
    },
    pick(user) {
      if (!this.mention) return
      const { start, end } = this.mention
      this.$emit('update:modelValue', withoutRange(this.modelValue, start, end))
      this.$emit('update:account', user)
      this.closeList()
      this.mention = null
      this.focus()
    },
    removeAccount() {
      this.$emit('update:account', null)
      this.focus()
    },
  },
}
</script>

<style lang="scss">
@use "../../mixins";

/* form. so that the general .input rules do not undo the flex row */
form.SearchField {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 0;
  height: 2.2em;
  padding: 0 0.3em;
  border-radius: var(--r-pill);
  line-height: normal;

  .search-field-input {
    flex: 1 1 auto;
    min-width: 0;
    height: 100%;
    padding: 0 0.3em;
    background: transparent;
    box-shadow: none;
    border: 0;
    color: var(--text);
    font: inherit;
  }

  .search-submit,
  .search-clear {
    display: inline-grid;
    place-items: center;
    width: 1.8em;
    height: 1.8em;
    border-radius: 50%;
    color: var(--textFaint);
    flex: none;

    &:hover {
      color: var(--text);
    }
  }

  /* the account whose posts are searched */
  .search-account {
    /* a MentionPill; the remove button makes the right edge */
    flex: 0 1 auto;
    min-width: 0;
    max-width: 50%;
    height: 1.7em;
    padding-right: 0.2em;
    margin: 0;
  }

  .search-clear {
    @include mixins.touch {
      @include mixins.tap-area;
    }
  }

  /* the text field is to the right: grow over the chip's name only */
  .search-account-remove {
    @include mixins.touch {
      @include mixins.tap-area(start);
    }
  }

  .search-account-remove {
    flex: none;
    display: inline-grid;
    place-items: center;
    width: 1.3em;
    height: 1.3em;
    border-radius: 50%;
    color: var(--textFaint);

    &:hover {
      color: var(--text);
    }
  }

  /* a menu surface (the popover classes) under the field, as wide as it */
  .search-suggestions {
    position: absolute;
    top: calc(100% + 0.3em);
    left: 0;
    right: 0;
    min-width: 0;
  }
}
</style>

<style lang="scss" src="../emoji_input/autocomplete.scss"></style>
